import 'keen-slider/keen-slider.min.css';
import { useRef, useState } from 'react';
import { Icon, IconID } from '@hub/common/Icon/Icon';
import { Modal } from '@hub/common/Modal/Modal';
import { WidenImage } from '@hub/common/Widen/WidenImage';
import { WidenVideo } from '@hub/common/Widen/WidenVideo';
import { useIsomorphicLayoutEffect } from '@hub/hooks/useIsomorphicLayoutEffect';
import type { TechnicalData } from '@hub/models/data';
import { useTranslation } from 'next-i18next';
// eslint-disable-next-line import/no-cycle
import { TechnicalTable } from '@hub/common/TechnicalTable/TechnicalTable';
import type { WidenMediaElement } from '@hub/libs/widen-utils';
import { MediaElementType } from '@hub/libs/widen-utils';
import { getAttributeValue } from '@hub/bearing-hub/ProductDetails/AkeneoProductData/attribute.helper';

export interface ProductMediaTeaserModalProps {
  media: WidenMediaElement;
  drawingTechnicalData?: TechnicalData;
}

export const ProductMediaTeaserModal = ({ media, drawingTechnicalData }: ProductMediaTeaserModalProps) => {
  const { t } = useTranslation(['product/productDetails', 'product/productMediaCarousel']);

  const isMountedRef = useRef<boolean>(false);

  useIsomorphicLayoutEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const headline = getAttributeValue<string>(media.heading);
  const { description } = media;
  const isDrawing = media.type === MediaElementType.DRAWING;

  return (
    <>
      <h4 className="w-full top-0 h-16 flex flex-col justify-center">{headline}</h4>
      <div data-testid="product-image-teaser-modal" className="relative m-auto mt-8" style={{ maxWidth: '1024px' }}>
        <div className="relative border border-gray-200 m-auto">
          {media.type === MediaElementType.IMAGE || isDrawing ? (
            <WidenImage
              width={1024}
              height={680}
              data-testid="product-image-teaser"
              alt={media.description || media.title}
              layout="intrinsic"
              shortCode={media.shortCode}
              filename={media.filename}
              title={media.title}
            />
          ) : (
            <WidenVideo
              data-testid="product-video-teaser"
              shortCode={media.shortCode}
              filename={media.filename}
              posterWidth={640}
              posterHeight={480}
              hd
              title={media.title}
            />
          )}
        </div>
        <div className="text-left">
          {description && !isDrawing && (
            <p
              data-testid="product-media-teaser-description"
              className="mt-6"
              dangerouslySetInnerHTML={{ __html: description || '' }}
            />
          )}
          {!isDrawing && (
            <p data-testid="product-media-teaser-similar-info" className="text-xxs mt-6">
              {t('PRODUCT_MEDIA_CAROUSEL.SIMILAR', { ns: 'product/productMediaCarousel' })}
            </p>
          )}
          {isDrawing && drawingTechnicalData && drawingTechnicalData && (
            <div className="mt-16" data-testid="product-media-teaser-drawing-technical-data">
              <TechnicalTable data={[drawingTechnicalData]} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export interface ProductMediaTeaserProps {
  media: WidenMediaElement;
  width: number;
  height: number;
  drawingTechnicalData?: TechnicalData;
  objectFit?: 'contain' | 'fill' | 'cover' | 'scale-down' | 'none';
}

export const ProductMediaTeaser = ({
  media,
  width,
  height,
  drawingTechnicalData,
  objectFit,
}: ProductMediaTeaserProps) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <div className="relative text-center max-w-full" data-testid="product-media-teaser">
      <div className="cursor-pointer border border-gray-200 relative" onClick={() => setShowModal(true)}>
        <WidenImage
          width={width}
          height={height}
          alt={media?.description || media?.title || ''}
          shortCode={media?.shortCode}
          filename={media?.filename}
          layout="responsive"
          title={media?.title}
          objectFit={objectFit}
        />
        <div className="absolute bottom-4 right-4">
          <Icon size={10} id={IconID.MAGNIFIER} primary={false} />
        </div>
      </div>

      <Modal visible={showModal} onClose={() => setShowModal(false)}>
        <ProductMediaTeaserModal media={media} drawingTechnicalData={drawingTechnicalData} />
      </Modal>
    </div>
  );
};
